import React, { useEffect, useState, useMemo, useCallback } from 'react';
// 🚀 WOUTER + ZUSTAND: Navigation optimisée avec store
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { useNavigationStore } from '../../stores/navigationStore';
import EventModal from './EventModal';
import PreviewCardMap from './PreviewCardMap';
import { Event } from '../../types/event.types';
import { getEventByIdDetailed } from '../../supabase/SmartListenerService';
import { SEO } from '../SEO';
import { Button, EventCardSkeleton, LogoSVG } from '../../pages/DesignSystem';
import './EventModalRoute.css';

interface LocationState {
  isMapView?: boolean;
  // Support both formats: {lat, lng} from store and {latitude, longitude} from location.state
  userLocation?: { latitude: number; longitude: number } | { lat: number; lng: number };
  returnToMulti?: boolean;
  multiEvents?: Event[];
  location?: string;
  event?: Event;
  returnTo?: string;
  seriesEvents?: Event[];
  showMultiEventModal?: boolean;
  enrichedEvents?: Array<{ main: Event; series: Event[]; _signature?: string }>;
}

const EventModalRoute: React.FC = () => {
  // 🚀 WOUTER: Récupérer depuis le store Zustand ET fallback location.state
  const { params, navigate, location } = useWouterRouter();
  const { eventModalState: storeState, previewCardMapState } = useNavigationStore();

  // Fallback: utiliser location.state si store vide (compatibilité)
  const locationState = (storeState || location.state) as LocationState;

  // 🆕 Extraire aussi seriesId pour les routes /favorites/series/:seriesId et /series/:seriesId
  const { id, seriesId, location: routeLocation } = params as { id?: string; seriesId?: string; location?: string };

  // Utiliser seriesId comme fallback si id n'est pas défini (routes de séries)
  const eventId = id || seriesId;

  // 🔧 FIX PRD Navigation Phase 3d : Détecter si on est sur une route PreviewCardMap
  const isPreviewCardMapRoute = window.location.pathname.startsWith('/events/');
  
  // 🎯 OPTIMISATION CIBLÉE: Mémoriser seulement les données qui comptent
  // 🚀 WOUTER: Mémoriser les données depuis store OU location.state
  const stableLocationData = useMemo(() => {
    // 🔧 FIX: Convertir userLocation de {lat, lng} (store) vers {latitude, longitude} (EventModal)
    const rawUserLocation = locationState?.userLocation;
    let normalizedUserLocation: { latitude: number; longitude: number } | undefined;

    if (rawUserLocation) {
      // Support both formats: {lat, lng} from store and {latitude, longitude} from location.state
      if ('lat' in rawUserLocation && 'lng' in rawUserLocation) {
        normalizedUserLocation = {
          latitude: (rawUserLocation as { lat: number; lng: number }).lat,
          longitude: (rawUserLocation as { lat: number; lng: number }).lng
        };
      } else if ('latitude' in rawUserLocation && 'longitude' in rawUserLocation) {
        normalizedUserLocation = rawUserLocation as { latitude: number; longitude: number };
      }
    }

    return {
      showMultiEventModal: locationState?.showMultiEventModal || false,
      multiEvents: locationState?.multiEvents || [],
      enrichedEvents: locationState?.enrichedEvents || [],
      isMapView: locationState?.isMapView || false,
      userLocation: normalizedUserLocation,
      location: locationState?.location || '',
      isMultiEventView: Boolean(locationState?.showMultiEventModal && (locationState?.multiEvents?.length || 0) > 0),
      source: storeState ? 'zustand-store' : 'location-state'
    };
  }, [
    locationState?.showMultiEventModal,
    locationState?.multiEvents?.length,
    locationState?.enrichedEvents?.length,
    locationState?.isMapView,
    locationState?.userLocation,
    locationState?.location,
    storeState ? 'store' : 'location' // Dépendance sur la source
  ]);

  const [event, setEvent] = useState<Event | null>(null);
  // ⚠️ loading=true UNIQUEMENT si un event est à charger (route /event/:id ou série).
  // Sur /events/:location il n'y a pas d'eventId : l'effet de chargement sortait sans
  // jamais repasser loading à false, et depuis le 08/09 l'écran de chargement est un
  // overlay plein écran (100dvh, fond blanc) → il masquait TOUTE l'app touriste
  // (liste + map) sur les pages villes. Avant le 08/09 ce même état bloqué ne rendait
  // qu'un <div>Chargement...</div> inoffensif, d'où l'absence de symptôme.
  const [loading, setLoading] = useState<boolean>(Boolean(eventId));
  // 🔧 FIX PRD Fiabilité découverte (item 4) : distinguer "vraiment introuvable" d'une
  // erreur réseau/cache, pour afficher un écran approprié (retry vs CTA vers la liste)
  const [loadError, setLoadError] = useState<'network_error' | null>(null);
  // Incrémenté par le bouton "Retry" pour redéclencher le chargement
  const [retryToken, setRetryToken] = useState(0);
  // ✅ État pour fermer visuellement le modal immédiatement
  const [isClosing, setIsClosing] = useState(false);

  // 🔧 FIX deep-link externe : si l'utilisateur arrive directement sur une URL de modal
  // (ex: lien partagé /events/mirissa/event/abc123 ouvert depuis Instagram), on injecte
  // l'URL parente comme entrée précédente d'historique. Ainsi `history.back()` lors de la
  // fermeture du modal ramène à `/events/mirissa` au lieu de quitter le site.
  //
  // Détection : `sessionStorage.w2d_entry_path` (posé par le shell script de index.html
  // au tout premier chargement) === pathname courant ⇒ on est sur la page d'entrée du
  // load actuel. `window.history.length` n'est pas fiable car un clic sur lien externe
  // dans un onglet pré-existant peut donner length=2 (avec Instagram comme entrée
  // précédente — un back() ramènerait à Instagram, donc hors site).
  //
  // S'exécute UNE SEULE FOIS au mount initial.
  useEffect(() => {
    const entryPath = (() => {
      try { return sessionStorage.getItem('w2d_entry_path'); } catch { return null; }
    })();
    const isDeepLinkEntry = entryPath !== null && entryPath === window.location.pathname;
    if (!isDeepLinkEntry) return;

    // Calcul de la route parente selon le pattern de l'URL courante
    const path = window.location.pathname;
    let parentUrl = '/events';
    if (path.startsWith('/favorites/')) {
      parentUrl = '/favorites';
    } else {
      const cityMatch = path.match(/^\/events\/([^/]+)/);
      if (cityMatch) parentUrl = `/events/${cityMatch[1]}`;
    }

    const currentUrl = path + window.location.search + window.location.hash;
    console.log('🔗 [EventModalRoute] Deep-link entry détectée → injection parent', { parentUrl, currentUrl });

    // Remplacer l'entrée actuelle par le parent, puis push l'URL courante par-dessus.
    // Résultat (vue fonctionnelle) : la prochaine entrée "back" pointe sur parentUrl.
    window.history.replaceState(null, '', parentUrl);
    window.history.pushState(null, '', currentUrl);

    // Effacer le marker pour qu'une re-navigation interne future vers une URL de modal
    // (ex: clic sur un autre event après avoir fermé celui-ci) ne se ré-injecte pas.
    try { sessionStorage.removeItem('w2d_entry_path'); } catch { /* ok */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔧 FIX PRD Navigation Phase 3d : Reset isClosing quand on est sur une route PreviewCardMap
  // Cela permet de réafficher PreviewCardMap après history.back() depuis EventModal
  useEffect(() => {
    if (isPreviewCardMapRoute && isClosing) {
      console.log('🔄 [EventModalRoute] Reset isClosing car on est sur route /events/');
      setIsClosing(false);
    }
  }, [isPreviewCardMapRoute, isClosing]);

  // ✅ Mémoriser la route initiale pour détecter les changements via bouton back navigateur
  const [initialEventId] = useState(eventId);

  // ✅ Écouter le bouton back du navigateur (popstate)
  useEffect(() => {
    const handlePopState = () => {
      // Si l'URL a changé depuis qu'on a ouvert cette modale, fermer
      const currentPath = window.location.pathname;
      const isStillOnThisEvent = currentPath === `/event/${initialEventId}`;

      if (!isStillOnThisEvent && !isClosing) {
        console.log('🔙 [EventModalRoute] Bouton back navigateur détecté, fermeture');
        setIsClosing(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [initialEventId, isClosing]);

  console.log('🎭 [EventModalRoute] Props:', {
    id,
    seriesId,
    eventId,
    routeLocation,
    pathname: location.pathname,
    showMultiEventModal: stableLocationData.showMultiEventModal,
    multiEventsLength: stableLocationData.multiEvents.length,
    hasValidState: Boolean(locationState)
  });

  // 🚀 SMART CACHE - Charger l'événement directement SEULEMENT si c'est une vue individuelle
  useEffect(() => {
    if (stableLocationData.isMultiEventView) {
      setLoading(false);
      return;
    }

    // Pas d'event à charger (route /events/:location) : ne jamais laisser loading
    // à true — voir le commentaire de l'état loading ci-dessus.
    if (!eventId) {
      setLoading(false);
      return;
    }

    const loadEvent = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // ✅ PRIORITÉ 1 : Utiliser l'événement depuis le state (RecurringEventCard, navigation depuis FavoritesPage)
        // 🔧 FIX: Chercher dans enrichedEvents OU multiEvents OU event (compatibilité)
        const stateEvent =
          locationState?.enrichedEvents?.[0]?.main ||
          locationState?.multiEvents?.[0] ||
          locationState?.event;

        // Vérifier que l'événement du state correspond à l'ID demandé
        if (stateEvent && stateEvent.id === eventId) {
          console.log('✅ [EventModalRoute] Utilisation event depuis state:', {
            source: locationState?.enrichedEvents?.[0]?.main ? 'enrichedEvents' :
                    locationState?.multiEvents?.[0] ? 'multiEvents' : 'event',
            eventId: stateEvent.id
          });
          setEvent(stateEvent);
          setLoading(false);
          return;
        }

        // ✅ PRIORITÉ 2 : Charger via getEventByIdDetailed
        // 🔧 FIX PRD Fiabilité découverte (item 4) : ce résultat distingue "not_found"
        // (event supprimé/dépublié) de "network_error" (retry possible), contrairement à
        // l'ancien getEventById qui retournait `null` dans les deux cas.
        const result = await getEventByIdDetailed(eventId);
        if (result.status === 'found') {
          setEvent(result.event);
        } else if (result.status === 'network_error') {
          setEvent(null);
          setLoadError('network_error');
        } else {
          setEvent(null);
        }
      } catch (error) {
        console.error('❌ Erreur chargement événement:', error);
        setEvent(null);
        setLoadError('network_error');
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [eventId, stableLocationData.isMultiEventView, locationState?.event, retryToken]); // Stabilité via stableLocationData

  // 🔧 FIX PRD Fiabilité découverte (item 4) : bouton "Retry" de l'écran erreur réseau
  const handleRetry = useCallback(() => {
    setRetryToken(token => token + 1);
  }, []);

  // 🔧 FIX PRD Fiabilité découverte (item 4) : CTA de l'écran "introuvable" → retour à la liste
  const handleBackToEvents = useCallback(() => {
    const cityMatch = window.location.pathname.match(/\/events\/([^/]+)/);
    const targetUrl = cityMatch ? `/events/${cityMatch[1]}` : '/events';
    navigate(targetUrl, { replace: true });
  }, [navigate]);

  const handleClose = () => {
    // ✅ Fermer visuellement le modal IMMÉDIATEMENT avant navigation
    if (isClosing) {
      console.log('🚫 [EventModalRoute] Fermeture déjà en cours, ignorée');
      return;
    }

    // 🔧 FIX PRD Navigation Phase 3d : Décider entre history.back() et navigate()
    // Si on a un previewCardMapState sauvegardé, on navigue vers /events/:location
    // Sinon, on utilise history.back() si c'est sûr, ou on va à / par défaut
    const hasPreviewCardMapState = previewCardMapState && previewCardMapState.multiEvents.length > 0;

    console.log('🔙 [EventModalRoute] Fermeture demandée:', {
      hasPreviewCardMapState,
      previewLocation: previewCardMapState?.location,
      historyLength: window.history.length
    });

    if (hasPreviewCardMapState) {
      // ✅ SOLUTION 1 : On a un état PreviewCardMap sauvegardé → naviguer vers /events/:location
      // PAS de setIsClosing(true) car on veut afficher PreviewCardMap immédiatement
      const targetPath = `/events/${encodeURIComponent(previewCardMapState.location || '')}`;
      console.log('🗺️ [EventModalRoute] Navigation vers PreviewCardMap:', targetPath);
      navigate(targetPath, { replace: true });
    } else {
      // ✅ SOLUTION 2 : Pas de PreviewCardMap → retour classique avec isClosing
      setIsClosing(true);

      // Vérifier si on peut utiliser history.back() en toute sécurité
      const canGoBack = window.history.length > 1;

      if (canGoBack) {
        console.log('🔙 [EventModalRoute] Retour via history.back()');
        window.history.back();
      } else {
        // 🔧 FIX: navigate to /events/:city (URL parente) — '/' est désormais la LandingPage
        const cityMatch = window.location.pathname.match(/\/events\/([^/]+)/);
        const targetUrl = cityMatch ? `/events/${cityMatch[1]}` : '/events';
        console.log('🏠 [EventModalRoute] Pas d\'historique, retour à', targetUrl);
        navigate(targetUrl, { replace: true });
      }
    }
  };

  const handleToggleFavorite = (eventId: string) => {
    // TODO: Implémenter avec SmartCache
    console.log('Toggle favorite:', eventId);
  };

  // ✅ Si on est en train de fermer (via overlay, escape, ou bouton back), ne rien afficher
  // 🔧 FIX PRD Navigation Phase 3d : SAUF si on est sur une route PreviewCardMap
  // Dans ce cas, on doit afficher PreviewCardMap même si isClosing était true précédemment
  if (isClosing && !isPreviewCardMapRoute) {
    console.log('🔒 [EventModalRoute] isClosing=true et pas sur route /events/, ne rien afficher');
    return null;
  }

  // 🔧 FIX PRD Fiabilité découverte (item 4) : écran de chargement on-brand (skeleton du
  // Design System) au lieu d'un <div>Chargement...</div> brut et en français
  if (loading) {
    return (
      <div className="event-modal-route__loading">
        <div className="event-modal-route__loading-card">
          <EventCardSkeleton />
        </div>
      </div>
    );
  }

  // 🔧 FIX PRD Navigation Phase 3c : Si on est sur /events/:location mais sans state valide
  // Essayer de restaurer depuis previewCardMapState du store (sauvegardé avant navigation vers EventModal)
  const isEventsRoute = window.location.pathname.startsWith('/events/');

  // Déterminer les données à utiliser : stableLocationData OU previewCardMapState
  const hasValidState = stableLocationData.isMultiEventView && stableLocationData.multiEvents.length > 0;
  const hasRestoredState = previewCardMapState && previewCardMapState.multiEvents.length > 0;

  // Si on est sur /events/:location sans state valide
  if (isEventsRoute && !hasValidState) {
    // 🔧 FIX PRD Navigation Phase 3c : Essayer de restaurer depuis le store
    if (hasRestoredState) {
      console.log('🔄 [EventModalRoute] Restauration PreviewCardMap depuis store:', {
        eventsCount: previewCardMapState.multiEvents.length,
        location: previewCardMapState.location
      });
      // On continue avec previewCardMapState
    } else {
      // 🔧 FIX: garder l'URL events au lieu de retourner à '/' (= LandingPage)
      const cityMatch = window.location.pathname.match(/\/events\/([^/]+)/);
      const targetUrl = cityMatch ? `/events/${cityMatch[1]}` : '/events';
      console.log('🔄 [EventModalRoute] Route /events/:location sans state valide, redirection vers', targetUrl);
      navigate(targetUrl, { replace: true });
      return null;
    }
  }

  // 🔍 Mode PreviewCardMap pour /events/location
  // Utiliser les données du store si disponibles, sinon stableLocationData
  const previewData = hasRestoredState && !hasValidState ? {
    multiEvents: previewCardMapState!.multiEvents,
    enrichedEvents: previewCardMapState!.enrichedEvents || [],
    location: previewCardMapState!.location,
    userLocation: previewCardMapState!.userLocation,
    isMapView: previewCardMapState!.isMapView
  } : stableLocationData;

  if ((hasValidState || hasRestoredState) && (previewData.multiEvents?.length || 0) > 0) {
    console.log('🗺️ [EventModalRoute] Rendu PreviewCardMap avec', previewData.multiEvents?.length || 0, 'événements', {
      source: hasRestoredState && !hasValidState ? 'previewCardMapState (restored)' : 'stableLocationData'
    });

    const handleEventClick = (event: Event) => {
      navigate(`/event/${event.id}`, {
        state: {
          returnToMulti: true,
          multiEvents: previewData.multiEvents,
          location: previewData.location,
          isMapView: previewData.isMapView,
          userLocation: previewData.userLocation,
          enrichedEvents: previewData.enrichedEvents
        }
      });
    };

    return (
      <PreviewCardMap
        events={previewData.multiEvents || []}
        isOpen={!isClosing}
        onClose={handleClose}
        onEventClick={handleEventClick}
        isSingleEvent={(previewData.multiEvents?.length || 0) === 1}
        location={previewData.location || ''}
        enrichedEvents={previewData.enrichedEvents} // 🆕 Données enrichies avec signature stable
      />
    );
  }

  // 🔍 Mode EventModal pour /event/id
  // 🔧 FIX PRD Fiabilité découverte (item 4) : écran "introuvable" on-brand (en anglais, comme
  // le reste de l'UI touriste), distinct de l'écran "erreur réseau" (retry possible) —
  // auparavant les deux cas affichaient le même <div>Événement introuvable</div> brut en français.
  if (!event) {
    if (loadError === 'network_error') {
      return (
        <div className="event-modal-route__state">
          <div className="event-modal-route__state-icon">
            <LogoSVG variant="what2Do" size="auto" padding={8} color="var(--primary-dark)" />
          </div>
          <h2>Couldn't load this event</h2>
          <p>Check your connection and try again.</p>
          <div className="event-modal-route__state-actions">
            <Button variant="primary" size="md" onClick={handleRetry}>
              Retry
            </Button>
            <Button variant="secondary" size="md" onClick={handleBackToEvents}>
              See events near me
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="event-modal-route__state">
        <div className="event-modal-route__state-icon">
          <LogoSVG variant="what2Do" size="auto" padding={8} color="var(--primary-dark)" />
        </div>
        <h2>Event not found</h2>
        <p>This event may have been removed or is no longer available.</p>
        <div className="event-modal-route__state-actions">
          <Button variant="primary" size="md" onClick={handleBackToEvents}>
            See events near me
          </Button>
        </div>
      </div>
    );
  }

  // 🔍 SEO: Construire les données pour le Schema.org Event
  const eventSEOData = {
    name: event.title,
    startDate: event.datetime?.toISOString() || new Date().toISOString(),
    endDate: event.endDateTime || event.datetime?.toISOString(),
    location: event.locationName || 'Sri Lanka',
    description: event.description || `${event.title} - Event in Sri Lanka`,
    image: event.image,
    organizer: event.organizer,
    price: event.prices?.[0]?.amount?.toString() || '0',
    currency: event.prices?.[0]?.currency || 'LKR'
  };

  return (
    <>
      {/* SEO dynamique pour la page event */}
      <SEO
        title={event.title}
        description={event.description?.slice(0, 160) || `${event.title} - Discover this event in ${event.locationName || 'Sri Lanka'}`}
        image={event.image}
        url={`/event/${event.id}`}
        type="event"
        event={eventSEOData}
      />
      <EventModal
      event={event}
      isOpen={!isClosing}
      onClose={handleClose}
      onFavorite={handleToggleFavorite}
      userLocation={stableLocationData.userLocation}
      seriesEvents={
        // 🚀 WOUTER: Récupérer seriesEvents depuis enrichedEvents ou fallback
        (() => {
          // 1. Chercher dans enrichedEvents si disponible
          if (stableLocationData.enrichedEvents?.length > 0) {
            const enrichedEvent = stableLocationData.enrichedEvents.find(
              enriched => enriched.main.id === event.id
            );
            if (enrichedEvent?.series && enrichedEvent.series.length > 0) {
              console.log('✅ [EventModalRoute] SeriesEvents depuis enrichedEvents:', enrichedEvent.series.length);
              return enrichedEvent.series;
            }
          }
          
          // 2. Fallback: ancien location.state.seriesEvents  
          if (locationState?.seriesEvents && locationState.seriesEvents.length > 0) {
            console.log('✅ [EventModalRoute] SeriesEvents depuis location.state (fallback):', locationState.seriesEvents.length);
            return locationState.seriesEvents;
          }
          
          console.log('⚠️ [EventModalRoute] Aucun seriesEvents trouvé - EventModal utilisera son fallback SmartCache');
          return [];
        })()
      }
    />
    </>
  );
};

export default EventModalRoute;